function normalizeTextParts(value) {
    if (Array.isArray(value)) {
        return value;
    }
    if (value === null || value === undefined) {
        return [];
    }
    return [ value ];
}

function toSearchText(value) {
    return normalizeTextParts(value)
        .map(part => String(part || '').trim().toLowerCase())
        .filter(Boolean)
        .join(' ');
}

function normalizeToken(token, minTokenLength) {
    const normalized = String(token || '').trim().toLowerCase();
    if (!normalized) {
        return '';
    }
    if (normalized.length < minTokenLength) {
        return '';
    }
    if (/^\d+$/.test(normalized)) {
        return '';
    }
    return normalized;
}

export function buildRankedTokens(items, {
    getItemKey,
    getSearchText,
    minTokenLength = 4,
} = {}) {
    const tokenToSources = new Map();
    const keyFn = getItemKey || ((item, index) => String(item?.id || item?.path || item?.name || `item-${index}`));
    const textFn = getSearchText || (item => item);

    (items || []).forEach((item, index) => {
        const sourceKey = keyFn(item, index);
        const sourceTokens = new Set();
        const rawText = toSearchText(textFn(item));

        rawText.split(/[^a-z0-9]+/i).forEach(token => {
            const normalized = normalizeToken(token, minTokenLength);
            if (normalized) {
                sourceTokens.add(normalized);
            }
        });

        sourceTokens.forEach(token => {
            if (!tokenToSources.has(token)) {
                tokenToSources.set(token, new Set());
            }
            tokenToSources.get(token).add(sourceKey);
        });
    });

    return Array.from(tokenToSources.entries())
        .map(([ token, sourceSet ]) => [ token, sourceSet.size ])
        .sort((a, b) => {
            if (b[1] !== a[1]) {
                return b[1] - a[1];
            }
            if (a[0].length !== b[0].length) {
                return a[0].length - b[0].length;
            }
            return a[0].localeCompare(b[0]);
        })
        .map(([ token ]) => token);
}

export function getSuggestedTokens(rankedTokens, activeTokens, query, {
    minQueryLength = 2,
    maxSuggestions = 6,
} = {}) {
    const q = String(query || '').trim().toLowerCase();
    if (!q || q.length < minQueryLength) {
        return [];
    }

    const activeSet = new Set((activeTokens || []).map(token => String(token || '').toLowerCase()));
    return (rankedTokens || [])
        .filter(token => !activeSet.has(token))
        .filter(token => token.startsWith(q) || token.includes(q))
        .slice(0, maxSuggestions);
}

export function itemMatchesActiveTokens(item, activeTokens, getSearchText) {
    if (!activeTokens || activeTokens.length < 1) {
        return true;
    }
    const haystack = toSearchText(getSearchText ? getSearchText(item) : item);
    return activeTokens.every(token => haystack.includes(String(token || '').toLowerCase()));
}

export function itemMatchesQuery(item, query, getSearchText) {
    const q = String(query || '').trim().toLowerCase();
    if (!q) {
        return true;
    }
    const haystack = toSearchText(getSearchText ? getSearchText(item) : item);
    return haystack.includes(q);
}
